// Table_0.jsx : 주문 이력

import { useState, useRef, useEffect, useCallback } from "react";
import "../../styles/Table.css";
import { isOrderInProgress } from "../../utils/orderStatus";

const Table_0 = ({
  userType,
  onOrderConfirm,
  onConfirmCancel,
  list,
  userId,
  handleOrderBookAddModifyBtn,
  onClickBookDeleteButton,
  setOrderBookEditMode,
  setButtonText,
  buttonText,
  setIsNewOrder,
  setOrderItemList,
  selectedOrderBookRowId,
  setSelectedOrderBookRowId,
  setSelectedOrderItemRowOIserial,
  setSelectedProductCode,
  setShowDeleteModal,
  setTargetTableToDelete,
  orderItemListLoadedForRowId,
  setClientQuery,
  clientQuery,
  clientInfo,
  orderItemList,
  columnWidths = ["50px", "80px", "100px", "100px", "80px", "80px"],
  columnAligns = ["center", "center", "left", "center", "left", "right"],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageGroup, setPageGroup] = useState(0);
  const itemsPerPage = 15;
  const tableContainerRef = useRef(null);
  const selectedRowRef = useRef(null);
  const prevListLengthRef = useRef(list.length);

  const totalPages = Math.ceil(list.length / itemsPerPage);
  const pagesPerGroup = 8;
  const totalGroups = Math.ceil(totalPages / pagesPerGroup);

  const startPage = pageGroup * pagesPerGroup + 1;
  const endPage = Math.min(startPage + pagesPerGroup - 1, totalPages);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = list.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    if (list.length > prevListLengthRef.current) {
      const lastPage = Math.ceil(list.length / itemsPerPage);
      setCurrentPage(lastPage);
      setPageGroup(Math.floor((lastPage - 1) / pagesPerGroup));
    }
    prevListLengthRef.current = list.length;
  }, [list]);

  const handleNextGroup = () => {
    if (pageGroup < totalGroups - 1) {
      setPageGroup(pageGroup + 1);
      setCurrentPage((pageGroup + 1) * pagesPerGroup + 1);
    }
  };

  const handlePrevGroup = () => {
    if (pageGroup > 0) {
      setPageGroup(pageGroup - 1);
      setCurrentPage((pageGroup - 1) * pagesPerGroup + 1);
    }
  };

  const goToPage = (number) => {
    setCurrentPage(number);
  };

  const handleOrderBookRowClick = useCallback(
    (data) => {
      if (selectedOrderBookRowId === data.OBserial) return;
      // 회사검색 박스에 선택 거래처 대입
      if (setClientQuery && typeof data.Client === "string") {
        setClientQuery(data.Client);
      }
      setSelectedOrderBookRowId(data.OBserial);
      setSelectedOrderItemRowOIserial(null);
      setSelectedProductCode(null);
      setIsNewOrder(false);

      // OrderStatus가 없거나, 출고/승인/확인이 아닌 경우에만 편집 가능
      const isLocked =
        data.OrderStatus?.includes("출고") ||
        data.OrderStatus?.includes("승인") ||
        data.OrderStatus?.includes("확인");

      if (!data.OrderStatus?.trim()) {
        setOrderBookEditMode(true);
        setButtonText("주문수정");
      } else if (isLocked) {
        setOrderBookEditMode(false);
        setButtonText("신규주문");
      } else {
        setOrderBookEditMode(true);
        setButtonText("주문수정");
      }
    },
    [
      selectedOrderBookRowId,
      setClientQuery,
      setSelectedOrderBookRowId,
      setSelectedOrderItemRowOIserial,
      setSelectedProductCode,
      setIsNewOrder,
      setOrderBookEditMode,
      setButtonText,
    ],
  );

  // Up/Down 키 핸들러
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      e.preventDefault();

      const currentIndex = list.findIndex(
        (item) => item.OBserial === selectedOrderBookRowId,
      );

      let nextIndex;
      if (currentIndex === -1) {
        // 선택된 행이 없으면 첫 번째 행 선택
        nextIndex = 0;
      } else if (e.key === "ArrowUp") {
        nextIndex = Math.max(0, currentIndex - 1);
      } else {
        nextIndex = Math.min(list.length - 1, currentIndex + 1);
      }

      if (nextIndex === currentIndex && currentIndex !== -1) return;

      const nextItem = list[nextIndex];
      if (!nextItem) return;

      // 페이지 전환 처리
      const nextPage = Math.floor(nextIndex / itemsPerPage) + 1;
      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
        setPageGroup(Math.floor((nextPage - 1) / pagesPerGroup));
      }

      handleOrderBookRowClick(nextItem);
    },
    [
      list,
      selectedOrderBookRowId,
      currentPage,
      pagesPerGroup,
      handleOrderBookRowClick,
    ],
  );

  const handleResetSelection = () => {
    setSelectedOrderBookRowId(null);
    setOrderBookEditMode(false);
    setButtonText("신규주문");
    setOrderItemList([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const year = dateString.slice(0, 2);
    const month = dateString.slice(2, 4);
    const day = dateString.slice(4, 6);
    return `20${year}/${month}/${day}`;
  };

  const getStatusColor = (status) => {
    if (!status) return "black";
    if (status === "주문확인") return "#28a745"; // clearer green (Bootstrap 'success')
    if (status === "주문완료") return "orange";
    return "black";
  };

  const formatInputTime = (inputTime) => {
    if (!inputTime) return "";

    const time = String(inputTime).match(/(?:-|T|\s)(\d{2})(\d{2})(\d{2})$/);
    return time ? `${time[1]}:${time[2]}:${time[3]}` : "";
  };

  // 삭제 버튼 공통 핸들러
  const handleDeleteClick = (e, obserial) => {
    e.stopPropagation();
    e.preventDefault();
    setTargetTableToDelete("BOOK");
    onClickBookDeleteButton(obserial);
    setShowDeleteModal(true);
  };

  const handleOrderConfirm = (obserial) => {
    setSelectedOrderBookRowId(obserial);
    onOrderConfirm(obserial);
  };

  // 확인취소는 별도 메시지 전송 없이 상태만 변경
  const handleConfirmCancel = (obserial) => {
    setSelectedOrderBookRowId(obserial);
    onConfirmCancel(obserial);
  };

  return (
    <>
      {/* 외부 wrapper: 전체 높이 고정, flex-column으로 테이블+페이지네이션 분리 */}
      <div
        style={{ display: "flex", flexDirection: "column", height: "750px" }}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={(e) => (e.currentTarget.style.outline = "none")}
      >
        {/* 테이블 스크롤 영역 */}
        <div
          className="table-container"
          ref={tableContainerRef}
          style={{ flex: 1, height: "auto", outline: "none", overflow: "auto" }}
        >
          <table className="table table-hover">
            <thead>
              <tr>
                <th style={{ width: columnWidths[0] || "auto" }}>주문번호</th>
                <th style={{ width: columnWidths[1] || "auto" }}>주문일자</th>
                {userType === "manager" && !userId && (
                  <th style={{ width: columnWidths[2] || "auto" }}>거래처명</th>
                )}
                <th style={{ width: columnWidths[3] || "auto" }}>주문상태</th>
                <th style={{ width: columnWidths[4] || "auto" }}>
                  고객요청사항
                </th>
                <th style={{ width: columnWidths[5] || "auto" }}>작 업</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((d) => {
                const isSelected = selectedOrderBookRowId === d.OBserial;
                const orderInProgress = isOrderInProgress(d.OrderStatus);
                const hasItemsForRow =
                  selectedOrderBookRowId === d.OBserial &&
                  Array.isArray(orderItemList) &&
                  orderItemList.length > 0;

                return (
                  <tr
                    key={d.OBserial}
                    ref={isSelected ? selectedRowRef : null}
                    onClick={() => handleOrderBookRowClick(d)}
                    className={isSelected ? "selected-row" : ""}
                  >
                    <td className={`align-${columnAligns[0]}`}>{d.OBserial}</td>
                    <td className={`align-${columnAligns[1]}`}>
                      {formatDate(d.OrderDate)}
                    </td>
                    {/*Client 열 조건부 렌더링 */}
                    {userType === "manager" && !userId && (
                      <td className={`align-${columnAligns[2]}`}>{d.Client}</td>
                    )}
                    <td className={`align-${columnAligns[3]}`}>
                      <span style={{ color: getStatusColor(d.OrderStatus) }}>
                        {d.OrderStatus}
                      </span>
                      {orderInProgress && (
                        <small className="order-status-input-time">
                          {formatInputTime(d.InputTime)}
                        </small>
                      )}
                    </td>
                    <td className={`align-${columnAligns[4] || "left"}`}>
                      <div
                        className="td-ellipsis"
                        title={d.clientRequest || ""}
                      >
                        {d.clientRequest || ""}
                      </div>
                    </td>
                    <td className={`align-${columnAligns[5]}`}>
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        {userType === "client" ? (
                          // 클라이언트: OrderStatus가 비어있거나 '주문중..'일 때만 삭제 가능
                          !d.OrderStatus || orderInProgress ? (
                            <button
                              onClick={(e) => handleDeleteClick(e, d.OBserial)}
                              className="btn-blue-outline"
                            >
                              삭제
                            </button>
                          ) : null
                        ) : (
                          // 관리자 모드
                          (() => {
                            if (
                              isSelected &&
                              orderItemListLoadedForRowId === d.OBserial &&
                              (!Array.isArray(orderItemList) ||
                                orderItemList.length === 0)
                            ) {
                              return (
                                <button
                                  onClick={(e) =>
                                    handleDeleteClick(e, d.OBserial)
                                  }
                                  className="btn-red-outline"
                                >
                                  삭제
                                </button>
                              );
                            }

                            // 주문상태가 '주문중..' 또는 비어있으면 기본적으로 '주문확인'
                            if (
                              orderInProgress ||
                              !d.OrderStatus
                            ) {
                              return (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleOrderConfirm(d.OBserial);
                                  }}
                                  className="btn-red-outline"
                                >
                                  주문확인
                                </button>
                              );
                            }

                            if (d.OrderStatus === "주문확인") {
                              return (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleConfirmCancel(d.OBserial);
                                  }}
                                  className="btn-blue-outline"
                                >
                                  확인취소
                                </button>
                              );
                            }

                            // 주문상태가 '전체출고' 또는 '승인완료'이면 버튼을 표시하지 않음
                            if (
                              d.OrderStatus?.trim() === "전체출고" ||
                              d.OrderStatus?.trim() === "승인완료"
                            ) {
                              return null;
                            }

                            // 그 외 상태는 주문확인 버튼으로 처리
                            return (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleOrderConfirm(d.OBserial);
                                }}
                                className="btn-red-outline"
                              >
                                주문확인
                              </button>
                            );
                          })()
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션: 항상 하단 고정 */}
        <div
          className="pagination order-history-pagination"
          style={{ flexShrink: 0 }}
        >
          <button onClick={handlePrevGroup} disabled={pageGroup === 0}>
            &laquo; 이전
          </button>

          {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
            const pageNum = startPage + i;
            return (
              <button
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                className={currentPage === pageNum ? "active" : ""}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={handleNextGroup}
            disabled={pageGroup >= totalGroups - 1}
          >
            다음 &raquo;
          </button>

          {userId &&
            (Array.isArray(clientInfo)
              ? clientInfo[0]?.ClientName
              : clientInfo?.ClientName) && (
              <button
                className="add-order-button"
                onClick={handleOrderBookAddModifyBtn}
                style={{ marginLeft: "12px" }}
              >
                {buttonText}
              </button>
            )}

          <button
            className={`row-select-release-button ${
              selectedOrderBookRowId ? "active" : ""
            }`}
            onClick={handleResetSelection}
            disabled={!selectedOrderBookRowId}
            style={{ marginLeft: "8px" }}
          >
            선택해제
          </button>
        </div>
      </div>
    </>
  );
};

export default Table_0;
